import { BusinessLogicError } from '@40seas/errors';
import { Resources } from '@40seas/frontend/dashboard/providers';
import { datadogRumProvider } from '@40seas/frontend/libs/providers';
import { getQueryFilter } from '@40seas/shared/query';
import baseAxios, {
    AxiosError,
    AxiosHeaders,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosRequestHeaders,
    InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry, { isNetworkError } from 'axios-retry';
import get from 'lodash/get';
import set from 'lodash/set';
import { DataProvider, GetManyParams, GetManyReferenceParams, GetManyResult, UpdateManyResult, UpdateParams } from 'react-admin';
import { UrlsProvider } from './urlsProvider';

const LARGE_FILTER_THRESHOLD = 50;

export default class DefaultDataProvider implements DataProvider {
    private readonly axios: AxiosInstance;

    constructor(
        protected readonly apiUrl: string,
        protected readonly urlsProvider: UrlsProvider,
        getHeaders: () => Promise<AxiosRequestHeaders>,
        private tenant?: string,
    ) {
        this.axios = baseAxios.create();

        axiosRetry(this.axios, {
            retries: 3,
            retryDelay: axiosRetry.exponentialDelay,
            retryCondition: (error: AxiosError) => isNetworkError(error),
            shouldResetTimeout: true,
        });

        this.axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
            const defaultHeaders = await getHeaders();

            const method = config.method?.toUpperCase();
            if (
                method &&
                method !== 'GET' &&
                this.tenant &&
                ['40SEAS-OVERVIEW', 'ZIM', 'SHIP4WD-OVERVIEW', 'NEKTOVA-OVERVIEW'].includes(this.tenant) &&
                !(
                    config.url?.includes('v1/users') ||
                    config.url?.includes('v1/users/suppliers') ||
                    config.url?.includes('v1/export') ||
                    config.url?.includes('v1/comments') ||
                    config.url?.match(/v1\/funds\/.*\/move(\/|$)/)
                )
            ) {
                alert('Cannot perform this action on overview tenant.');
                throw new Error('Cannot perform this action on overview tenant.');
            }

            return {
                ...config,
                headers: new AxiosHeaders({ ...defaultHeaders, ...config.headers }),
            };
        });

        this.axios.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => {
                const failedRequestUrl = error.response?.request?.responseURL;

                const errorMessage = datadogRumProvider.getErrorMessage(error);

                // probably a network error
                // we currently don't report those
                if (!failedRequestUrl) {
                    console.warn(errorMessage, error.config?.url);
                    const networkError = new BusinessLogicError('A network error has occurred. Please try again.');
                    // remove stack so it won't be reported to datadog
                    delete networkError.stack;
                    throw networkError;
                }

                // Don't report 404 errors to Datadog - these are expected client errors, not application bugs
                if (error.response?.status !== 404) {
                    datadogRumProvider.reportToDatadog(
                        set(
                            error,
                            ['response', 'data', 'message'],
                            `Fetching ${failedRequestUrl} failed with the following error: ${errorMessage}`,
                        ),
                    );
                }

                throw errorMessage;
            },
        );
    }

    async getList(resource: string, params: any) {
        const { filter: paramsFilter = {}, ...otherParams } = params;

        const { meta } = otherParams;
        const { headers, ...rawFilter } = paramsFilter;

        const filter = await this.resolveChargesPaymentMethodTypeFilter(resource, rawFilter);

        const path = meta?.resourceSuffix ? `${resource}/${meta.resourceSuffix}` : resource;

        if (this.hasLargeInFilter(filter)) {
            return this.getListWithPost(path, { ...otherParams, filter }, headers);
        }

        const {
            data: { results, count, aggregate },
        } = await this.axios.get(this.urlsProvider.getListUrl(path, { ...otherParams, filter }), { headers });

        return {
            data: results,
            total: count,
            aggregate,
        };
    }

    /**
     * Resolve Charges list filter: replace paymentMethodType (method types) with paymentMethod: { in: ids }.
     * Public so that count/tabs and other callers can use the same filter shape as getList.
     */
    async resolveChargesFilter(filter: Record<string, any>): Promise<Record<string, any>> {
        return this.resolveChargesPaymentMethodTypeFilter(Resources.Charges, filter ?? {});
    }

    /**
     * For Charges resource: translate paymentMethodType filter (e.g. ['wire', 'card']) into
     * paymentMethod: { in: [id1, id2, ...] } by fetching matching PaymentMethod IDs.
     */
    private async resolveChargesPaymentMethodTypeFilter(resource: string, filter: Record<string, any>): Promise<Record<string, any>> {
        if (resource !== Resources.Charges || !filter?.paymentMethodType) {
            return filter;
        }
        const types = filter.paymentMethodType?.in;
        const { paymentMethodType: _removed, ...rest } = filter;
        if (!Array.isArray(types) || types.length === 0) {
            return rest;
        }
        const { data: paymentMethods } = await this.getList(Resources.PaymentMethods, {
            filter: { methodType: { in: types } },
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'id', order: 'ASC' },
        });
        const ids = paymentMethods.map((pm: { id: string }) => pm.id);
        return ids.length ? { ...rest, paymentMethod: { in: ids } } : rest;
    }

    private hasLargeInFilter(filter: Record<string, any>): boolean {
        if (!filter) return false;

        for (const value of Object.values(filter)) {
            if (value && typeof value === 'object' && 'in' in value) {
                const inValue = value.in;
                if (Array.isArray(inValue) && inValue.length > LARGE_FILTER_THRESHOLD) {
                    return true;
                }
            }
        }
        return false;
    }

    private async getListWithPost(resource: string, params: any, headers?: any) {
        const { pagination, sort, filter, meta } = params;

        const queryBody = {
            filter: getQueryFilter(filter),
            sort: sort?.field && sort.field !== 'id' ? { [sort.field]: sort.order === 'DESC' ? '-1' : '1' } : undefined,
            limit: pagination?.perPage,
            page: pagination?.page,
            expand: meta?.expand,
            distinctBy: meta?.distinctBy,
            aggregate: meta?.aggregate,
        };

        const {
            data: { results, count, aggregate },
        } = await this.axios.post(`${this.apiUrl}/${resource}/query`, queryBody, { headers });

        return {
            data: results,
            total: count,
            aggregate,
        };
    }

    async getOne(resource: string, params: any) {
        const { meta } = params;

        const { headers } = meta || {};

        return this.axios.get(this.urlsProvider.getOneUrl(resource, params), { headers });
    }

    async getMany(resource: string, params: GetManyParams): Promise<GetManyResult<any>> {
        const target = params?.meta?.target ?? 'id__in';
        const queryParams = params?.meta?.queryParams;

        const { data } = await this.axios.get(this.urlsProvider.getManyUrl(resource, params, target, queryParams));

        // override ids as per getMany's interface
        // see https://marmelab.com/react-admin/DataProviderWriting.html#request-format

        const results = Array.isArray(data) ? data : data.results;

        return { data: results.map((result: any) => ({ originalId: result.id, ...result, id: getTargetId(result, target) })) };
    }

    async getManyReference(resource: string, params: GetManyReferenceParams) {
        const {
            data: { results, count },
        } = await this.axios.get(this.urlsProvider.getManyReferenceUrl(resource, params));
        return { data: results, total: count };
    }

    create(resource: string, params: any, config?: AxiosRequestConfig) {
        const { meta, data } = params;

        const { headers } = meta || {};

        return this.axios.post(this.urlsProvider.getCreateUrl(resource, params), data, {
            headers: { '40seas-tenant': this.tenant, ...headers },
            ...config,
        });
    }

    async update(resource: string, params: UpdateParams<any>) {
        const { meta, data, previousData } = params;

        const { headers = {} } = meta || {};

        if (previousData?.tenantId) {
            headers['40seas-tenant'] = previousData.tenantId;
        }

        return this.axios.put(this.urlsProvider.getUpdateUrl(resource, params), data, { headers });
    }

    async updateMany(resource: string, params: any): Promise<UpdateManyResult> {
        throw 'updateMany not implemented exception';
    }

    async delete(resource: string, params: any) {
        const { meta } = params;

        const { headers = {} } = meta || {};

        return this.axios.delete(this.urlsProvider.getDeleteUrl(resource, params), { headers });
    }

    async deleteMany(resource: string, params: any) {
        const deletions: Promise<any>[] = [];
        params.ids.forEach(async (id: string) => {
            deletions.push(this.axios.delete(this.urlsProvider.getDeleteUrl(resource, { id })));
        }, this);
        await Promise.all(deletions);
        return { data: params.ids };
    }

    async customGet(resource: string, path: string, params?: object, config?: AxiosRequestConfig) {
        return this.axios.get(`${this.apiUrl}/${resource}/${path}`, { ...config, params });
    }

    async customPost<T = any>(resource: string, path: string, data?: any, config?: AxiosRequestConfig) {
        return this.axios.post<T>(this.urlsProvider.getPathUrl(resource, path), data, config);
    }

    async customPut<T = any>(resource: string, path: string, data?: any, config?: AxiosRequestConfig) {
        return this.axios.put<T>(this.urlsProvider.getPathUrl(resource, path), data, config);
    }

    async getResourceProperty(resource: Resources, property: string, params?: object, config?: AxiosRequestConfig) {
        return this.customGet(resource, property, params, config);
    }

    async postResourceProperty<T = any>(resource: Resources, property: string, data?: any, config?: AxiosRequestConfig) {
        return this.customPost<T>(resource, property, data, config);
    }
}

export function getTargetId(record, target) {
    const targetName = target.split('__')[0];
    const targetValue = get(record, targetName);

    if (typeof targetValue === 'object') {
        return targetValue.id;
    }
    return targetValue;
}
